import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';

/**
 * Returns the contentful data for the bag.
 */
export const useBagContent = () => {
  const contentfulData = useContentfulContentTypeEntry<BagContent>({
    contentType: 'bag',
  });

  const content = contentfulData.data?.fields;
  const { formatMessage } = useIntl();

  return useMemo(
    () => ({
      viewMenuCta: content?.viewMenuCta ?? formatMessage(fallbacks.viewMenuCta), // prettier-ignore
      viewMenuA11y: content?.viewMenuA11y ?? formatMessage(fallbacks.viewMenuA11y), // prettier-ignore
      continueToCheckoutCta: content?.continueToCheckoutCta ?? formatMessage(fallbacks.continueToCheckoutCta), // prettier-ignore
      deliveryDisclosure: content?.deliveryDisclosureV2 ?? formatMessage(fallbacks.deliveryDisclosure, deliveryPlaceholders), // prettier-ignore
      outpostDisclosure: content?.outpostDisclosure ?? formatMessage(fallbacks.outpostDisclosure), // prettier-ignore
    }),
    [content, formatMessage],
  );
};

// ─── Fallbacks ──────────────────────────────────────────────────────────────

const fallbacks = defineMessages({
  viewMenuCta: {
    defaultMessage: 'Menu',
    description: 'Bag | CTAs | Menu',
  },
  viewMenuA11y: {
    defaultMessage: 'View menu',
    description: 'Bag | CTAs | View menu',
  },
  continueToCheckoutCta: {
    defaultMessage: 'Continue to checkout',
    description: 'Bag | CTAs | Continue to checkout',
  },
  deliveryDisclosure: {
    defaultMessage:
      'Delivery has a {minimum} minimum and includes higher menu prices and a {fee} delivery fee that goes directly to our delivery partner.',
    description: 'Bag | CTAs | Delivery Disclosure',
  },
  outpostDisclosure: {
    defaultMessage: 'Higher menu prices apply for Outpost.',
    description: 'Bag | CTAs | Outpost Disclosure',
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const deliveryPlaceholders = { minimum: '{minimum}', fee: '{fee}' };

// ─── Types ──────────────────────────────────────────────────────────────────

type BagContent = {
  viewMenuCta: string;
  viewMenuA11y: string;
  continueToCheckoutCta: string;
  deliveryDisclosureV2: string;
  outpostDisclosure: string;
};
